// Sheet del flow de cualquier preset: cubre toda la pantalla (mobile)
// o el área a la derecha del sidebar (desktop) y maneja todos los
// `step` del state machine: preview → choose-product → pick-product →
// upload-product → configure (con la rama paralela select-vistas →
// configure cuando es 'catalogo-ia').
//
// Ojo: este componente es el "núcleo" del browse de los modos
// `template`/`design`/`design-modelo`/`design-auto`/`catalogo-ia`. El
// modo `asistente` usa su propio sheet (AsistenteMarketingSheet) en
// vez de éste — el padre conmuta entre los dos en la rama del JSX.

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Plus,
  Sparkles,
} from 'lucide-react';
import type { MockProductV2, PresetV2 } from '../mockData';
import type {
  ModelPickerView,
  ModelSelection,
} from '../ModelPicker';
import type {
  FondoPickerView,
  FondoSelection,
} from '../FondoPicker';
import type { PoseV2 } from '../posesApi';
import type { FondoBiblioteca, FondoGuardado } from '../fondosApi';
import type { ModelV2 } from '../modelsApi';
import type { VistaCatalogo } from '../vistasCatalogo';
import { ChooseProductPanel } from './ChooseProductPanel';
import { PickProductPanel } from './PickProductPanel';
import { UploadProductPanel } from './UploadProductPanel';
import { VistaPickerFullscreen } from './VistaPickerFullscreen';
import {
  CatalogoCardsFullscreen,
  type CatalogoCard,
} from './CatalogoCardsFullscreen';
import { ConfigurePanel } from '../panels/ConfigurePanel';
import { MultiPresetCarousel, MultiSelectedStrip } from './MultiPresetUI';

// ─── Preset detail sheet ────────────────────────────────────────────────
// Cubre toda la pantalla. Arriba: la imagen del preset en grande (hero).
// Abajo: un container que cambia entre dos vistas según `step`:
//   • choose-product → "Subí la imagen del producto" + CTA "Ver mis productos"
//   • configure      → "¿Querés ajustar la generación?" + chips Pose/Formato
//                      + textarea libre + botón Generar
//
// Backdrop fade-in + sheet con slide-up de 300ms ease-out. Cerrar
// disponible vía X arriba a la derecha y tap en el backdrop.
export interface PresetDetailSheetProps {
  preset: PresetV2;
  animated: boolean;
  step:
    | 'preview'
    | 'choose-product'
    | 'pick-product'
    | 'upload-product'
    | 'select-vistas'
    | 'configure'
    | 'asistente';
  /** Lista de presets para swipear en el step 'preview'. */
  previewList: PresetV2[];
  /** Lista de productos elegidos (multi-selección). Vacío = ninguno aún. */
  selectedProducts: MockProductV2[];
  modelSelection: ModelSelection | null;
  fondoVal: string | null;
  formatoVal: string | null;
  otraConfig: string;
  sidebarWidth: number;
  onClose: () => void;
  /** El user tocó "Utilizar" en preview — avanza a choose-product con ese preset. */
  onUtilizarPreset: (p: PresetV2) => void;
  /** El user activó multi-select en preview y eligió N plantillas. Solo
   *  aplica al kind 'template'. La primera se usa como anchor visual. */
  onUtilizarMultiple: (presets: PresetV2[]) => void;
  /** IDs de plantillas seleccionadas en multi-select. Cuando hay más de
   *  una, la imagen del preset detail sheet se reemplaza por un carrusel
   *  de las plantillas seleccionadas. */
  multiPresetIds: string[];
  onShowProducts: () => void;
  /** "Añadir producto al outfit" desde configure — abre el picker de
   *  productos con la selección actual premarcada para sumar más. */
  onAddProducts: () => void;
  /** El user confirmó su selección (uno o varios productos). */
  onConfirmProducts: (products: MockProductV2[]) => void;
  onBackToChoose: () => void;
  /** Vacía la selección actual y vuelve al step 'choose-product'. */
  onClearProducts: () => void;
  // ── Upload de producto nuevo (step 'upload-product') ────────────
  /** Disparado desde ChooseProductPanel al seleccionar archivos. */
  onStartUpload: (files: FileList | File[]) => void;
  onAddMoreFiles: (files: FileList | File[]) => void;
  onRemovePendingFile: (idx: number) => void;
  onCancelUpload: () => void;
  onConfirmUpload: () => void;
  pendingProductFiles: File[];
  pendingProductName: string;
  onPendingNameChange: (v: string) => void;
  uploadingProduct: boolean;
  uploadError: string | null;
  /** Setter del modelo (preset/auto/custom/upload). */
  onModelChange: (sel: ModelSelection) => void;
  // ── Modo "varios modelos" (UI-only) ─────────────────────────────
  modelSelectionsExtra: (ModelSelection | null)[];
  onModelChangeExtras: (next: (ModelSelection | null)[]) => void;
  multiModelEnabled: boolean;
  onMultiModelEnabledChange: (v: boolean) => void;
  /** Abre el sheet de modelo en mobile. */
  onOpenModelPicker: () => void;
  /** Abre el sheet directo a una vista. Lo usa design-modelo desktop
   *  cuando el user toca "Creá tu modelo" o "Cargá modelo". */
  onOpenModelSheet: (initialView: ModelPickerView) => void;
  /** Variante del anterior para el sub-flow de multi: abre el sheet
   *  pre-configurado para editar un slot específico con la sub-vista
   *  pedida ('create' o 'upload'). */
  onOpenModelSheetForMultiSlot: (
    slotIdx: number,
    initialView: 'create' | 'upload',
  ) => void;
  /** Productos asignados por slot (modo varios modelos). */
  multiProductsPerSlot: Record<number, MockProductV2[]>;
  /** Tap en "Utilizando"/miniaturas de un slot del MultiView. */
  onTapSlotProducts: (slotIdx: number) => void;
  /** Abre el FondoPickerSheet directo a una vista — para "Describí el
   *  fondo" o "Cargá tu fondo" en desktop. */
  onOpenFondoSheet: (initialView: FondoPickerView) => void;
  onFondoChange: (v: string | null) => void;
  onFormatoChange: (v: string | null) => void;
  /** Abre el picker de formato (sheet/side panel). */
  onOpenFormatPicker: () => void;
  onOtraChange: (v: string) => void;
  onGenerate: () => void;
  // ── Modo 'design-auto' (cantidad de imágenes) ────────────────────
  /** Cantidad de imágenes a generar — solo se usa cuando el preset es
   *  'design-auto'. Controla el +/- arriba del Generar. */
  autoImageCount: number;
  onAutoImageCountChange: (v: number) => void;
  /** Tope superior del autoImageCount (mínimo siempre es 1). */
  autoImageCountMax: number;
  /** Modelos reales (para el ModelPickerInline desktop). */
  userModels: ModelV2[];
  userAuthId: string | null;
  userCompanyId: string | null;
  onModelCreated: () => void;
  // ── Modo 'design-modelo' ─────────────────────────────────────────────
  allPoses: PoseV2[];
  poseIds: string[];
  onPoseIdsChange: (ids: string[]) => void;
  onOpenPosePicker: () => void;
  bibliotecaFondos: FondoBiblioteca[];
  fondosGuardados: FondoGuardado[];
  fondoSelection: FondoSelection | null;
  onFondoSelectionChange: (sel: FondoSelection) => void;
  onOpenFondoPicker: () => void;
  fondoView: FondoPickerView;
  onFondoViewChange: (v: FondoPickerView) => void;
  onFondoSaved: () => void;
  /** Rubro de la empresa — para que el product picker active la sección
   *  "Sugeridos" solo en rubros de moda. */
  rubro: string | null;
  /** Si true, el ConfigurePanel bloquea el add/remove de slots del
   *  multi-modelo. Usado por E-commerce (kind: 'design') con poses
   *  de cantidad fija. */
  lockSlotCount?: boolean;
  // ── Modo 'catalogo-ia' ───────────────────────────────────────────────
  /** Vistas elegidas en el step `select-vistas`. */
  catalogoVistas: VistaCatalogo[];
  /** Setter del array de vistas elegidas. */
  onCatalogoVistasChange: (next: VistaCatalogo[]) => void;
  /** Confirma la selección de vistas y pasa al step `configure`. */
  onConfirmCatalogoVistas: () => void;
  /** Vuelve del step `configure` (cards review) al `select-vistas`. */
  onBackFromCatalogoConfigure: () => void;
  /** Dispara la generación del catálogo con las combinaciones que
   *  quedaron activas después del review (post-X de descartes). */
  onGenerateCatalog: (cards: CatalogoCard[]) => void;
  /** Marca activa — para el sheet de productos secundarios del catálogo
   *  (asociar producto nuevo subido). */
  brandId: string | null;
  /** Refresca el contexto global de userProducts tras subir uno nuevo
   *  desde el sheet de productos secundarios. */
  onProductsRefreshed: () => void;
  // ── Modo video (toggle "Imagen / Video" en el header) ──────────────
  /** Tab seleccionado en el header. Cuando es 'video' y el preset es
   *  'template', el ConfigurePanel agrega el botón "Configurar video"
   *  y la generación encadena imagen → prompt → video. */
  previewMediaTab: 'imagen' | 'video';
  /** Cambia el tab — usado por el toggle desktop dentro del preview
   *  (el mobile lo controla via mobileHeaderCenter). */
  onPreviewMediaTabChange: (tab: 'imagen' | 'video') => void;
  /** Segundos por escena para el video. Se usa al calcular el costo y
   *  al armar el payload de generación. */
  videoDuration: number;
  /** Si true, se mandará al endpoint multi-escena (`/video_cinema`) que
   *  cuesta el doble de créditos. */
  videoMultiScene: boolean;
  /** Costo en créditos del VIDEO por cada imagen generada (duración *
   *  multiplicador). El total final = creditCost base × cantidad imágenes
   *  + videoCreditCostPerImage × cantidad imágenes. */
  videoCreditCostPerImage: number;
  /** Abre el sheet de configuración de video. */
  onOpenVideoConfig: () => void;
  // ── Modo 'anuncio' ──────────────────────────────────────────────────
  /** State + handlers de los 3 campos textuales del modo Anuncio. Cada
   *  uno es `{ mode: 'auto'|'custom'|'hidden', value: string }`. */
  anuncioTitulo?: import('../panels/AnuncioPanel').AnuncioFieldState;
  onAnuncioTituloChange?: (
    next: import('../panels/AnuncioPanel').AnuncioFieldState,
  ) => void;
  anuncioDescripcion?: import('../panels/AnuncioPanel').AnuncioFieldState;
  onAnuncioDescripcionChange?: (
    next: import('../panels/AnuncioPanel').AnuncioFieldState,
  ) => void;
  anuncioCta?: import('../panels/AnuncioPanel').AnuncioFieldState;
  onAnuncioCtaChange?: (
    next: import('../panels/AnuncioPanel').AnuncioFieldState,
  ) => void;
  anuncioColors?: string[];
  onAnuncioColorsChange?: (next: string[]) => void;
  /** Paleta de la marca activa (`brand_profiles.colors`) — default del
   *  color picker. */
  anuncioBrandColors?: string[];
}
export const PresetDetailSheet: React.FC<PresetDetailSheetProps> = ({
  preset,
  animated,
  step,
  previewList,
  rubro,
  selectedProducts,
  modelSelection,
  fondoVal,
  formatoVal,
  otraConfig,
  sidebarWidth,
  onClose,
  onUtilizarPreset,
  onUtilizarMultiple,
  multiPresetIds,
  onShowProducts,
  onAddProducts,
  onConfirmProducts,
  onBackToChoose,
  onClearProducts,
  onStartUpload,
  onAddMoreFiles,
  onRemovePendingFile,
  onCancelUpload,
  onConfirmUpload,
  pendingProductFiles,
  pendingProductName,
  onPendingNameChange,
  uploadingProduct,
  uploadError,
  onModelChange,
  modelSelectionsExtra,
  onModelChangeExtras,
  multiModelEnabled,
  onMultiModelEnabledChange,
  onOpenModelPicker,
  onOpenModelSheet,
  onOpenModelSheetForMultiSlot,
  multiProductsPerSlot,
  onTapSlotProducts,
  onOpenFondoSheet,
  onFondoChange,
  onFormatoChange,
  onOpenFormatPicker,
  onOtraChange,
  onGenerate,
  autoImageCount,
  onAutoImageCountChange,
  autoImageCountMax,
  userModels,
  userAuthId,
  userCompanyId,
  onModelCreated,
  allPoses,
  poseIds,
  onPoseIdsChange,
  onOpenPosePicker,
  bibliotecaFondos,
  fondosGuardados,
  fondoSelection,
  onFondoSelectionChange,
  onOpenFondoPicker,
  fondoView,
  onFondoViewChange,
  onFondoSaved,
  lockSlotCount = false,
  catalogoVistas,
  onCatalogoVistasChange,
  onConfirmCatalogoVistas,
  onBackFromCatalogoConfigure,
  onGenerateCatalog,
  brandId,
  onProductsRefreshed,
  previewMediaTab,
  onPreviewMediaTabChange,
  videoDuration,
  videoMultiScene,
  videoCreditCostPerImage,
  onOpenVideoConfig,
  anuncioTitulo,
  onAnuncioTituloChange,
  anuncioDescripcion,
  onAnuncioDescripcionChange,
  anuncioCta,
  onAnuncioCtaChange,
  anuncioColors,
  onAnuncioColorsChange,
  anuncioBrandColors,
}) => {
  // Helpers de kind:
  //  • isDesignFlow agrupa los kinds que comparten layout del sheet
  //    (sin preview, sin imagen full-bleed, image scaled object-contain
  //    en choose-product/upload-product, configure mobile full-screen y
  //    sticky Generate). Incluye design-modelo, design-auto y anuncio.
  //  • isFullScreenConfigure identifica los kinds cuyo step `configure`
  //    ocupa TODO el panel en DESKTOP (aside full-width transparente, sin
  //    imagen a la izquierda, scroll de ventana y Generar flotante en el
  //    header). Aplica a 'design-modelo' (muchas opciones: Modelo + Pose +
  //    Fondo + Formato) y a 'anuncio' (productos + 3 textos + colores +
  //    formato). 'design-auto' NO — tiene pocas opciones (solo Formato +
  //    Instrucción) y se mantiene en el aside lateral derecho. Esto solo
  //    afecta el layout desktop; mobile usa su propio bloque y no cambia.
  const isDesignFlow =
    preset.kind === 'design-modelo' ||
    preset.kind === 'design-auto' ||
    preset.kind === 'anuncio';
  const isFullScreenConfigure =
    preset.kind === 'design-modelo' || preset.kind === 'anuncio';

  // Costo en créditos/tokens del preset — se muestra en el pill al
  // costado del texto en todos los botones "Generar" (el del
  // ConfigurePanel y el flotante de design-modelo desktop).
  //   • unidad 'segundo' → ratePer × durationSeconds (video).
  //   • cualquier otra unidad → ratePer × 1.
  //   • design-auto → multiplicamos también por autoImageCount
  //     (1-6 imágenes que pide el usuario).
  //   • design-modelo con multi-modelo → la generación produce 1
  //     imagen con todos los modelos juntos, no multiplicamos.
  //   • multi-preset (templates) → multiplicamos por la cantidad
  //     seleccionada.
  // ¿Está activo el modo video? Solo aplica al kind 'template' — los
  // otros kinds tienen su propio flow y no respetan este toggle.
  const isVideoModeActive =
    previewMediaTab === 'video' && preset.kind === 'template';
  const creditCost = (() => {
    const ratePer = preset.creditosPorUnidad;
    if (ratePer == null) return null;
    const unitMul =
      preset.unidadConsumo === 'segundo'
        ? preset.durationSeconds || 1
        : 1;
    const presetCount = multiPresetIds.length > 0 ? multiPresetIds.length : 1;
    // 'design-auto' y 'anuncio' generan `autoImageCount` imágenes (el
    // control de Cantidad), así que el costo escala con esa cantidad.
    const autoCountMul =
      preset.kind === 'design-auto' || preset.kind === 'anuncio'
        ? autoImageCount
        : 1;
    // design-modelo (single-modelo) genera 1 imagen por pose seleccionada
    // — el backend itera el array de `pose` y devuelve 1 imagen por cada
    // entry. En multi-modelo no aplica: ese flow tiene pose single-select.
    const poseCountMul =
      preset.kind === 'design-modelo' && !multiModelEnabled
        ? Math.max(1, poseIds.length)
        : 1;
    // En multi-modelo (design-modelo con varios modelos, o design /
    // E-commerce con pose 2+ personas) el backend genera 1 imagen por
    // cada modelo extra. El multiplicador es `1 + extras` = total de
    // personas / slots en la composición.
    const modelCountMul =
      (preset.kind === 'design-modelo' || preset.kind === 'design') &&
      multiModelEnabled
        ? 1 + modelSelectionsExtra.length
        : 1;
    const imageCost =
      ratePer *
      unitMul *
      presetCount *
      autoCountMul *
      poseCountMul *
      modelCountMul;
    // Cuando el modo es video, sumamos el costo de animar cada imagen
    // generada (1 video por imagen). El total que ve el user en el botón
    // de Generar = imágenes + videos.
    const totalImages =
      presetCount * autoCountMul * poseCountMul * modelCountMul;
    const videoCost = isVideoModeActive
      ? videoCreditCostPerImage * totalImages
      : 0;
    return imageCost + videoCost;
  })();

  // Index para navegar el preview en DESKTOP. Solo se inicializa con el
  // preset que abrió el sheet — en mobile cada card se renderiza
  // independiente, no se usa este state.
  const [previewIndex, setPreviewIndex] = useState<number>(() => {
    const i = previewList.findIndex((p) => p.id === preset.id);
    return i >= 0 ? i : 0;
  });

  // `panelOpen` controla el slide-up del panel inferior (steps !=
  // preview) animando max-height de 0 → valor final. Eso causa que la
  // imagen (flex-1 arriba) también anime su altura por el cambio de
  // flex space, dando el efecto "panel sube mientras la imagen se
  // acomoda al espacio restante" pedido por el user. Reset a false
  // cuando volvemos a preview.
  const [panelOpen, setPanelOpen] = useState<boolean>(step !== 'preview');

  // Ref al scroll container del preview mobile — necesario para
  // sincronizar el scroll inicial con el `previewIndex` resuelto
  // desde el preset tappeado.
  const mobilePreviewScrollRef = useRef<HTMLDivElement | null>(null);

  // Step "renderizado": sigue a `step` pero retrasa la transición
  // hacia 'preview' por 300ms para que el panel inferior alcance a
  // animar su salida (max-height → 0) ANTES de que el render
  // condicional cambie de rama. Sin esto, al volver de
  // choose-product el panel desaparecía instantáneo.
  const [renderedStep, setRenderedStep] = useState(step);

  // Effect 1: sync `renderedStep` con `step`.
  //   OUT (→ preview): primero cerrar el panel (panelOpen=false), 300ms
  //   después cambiar renderedStep para que el render cambie de rama.
  //   IN (preview → otro): cambiar renderedStep inmediato → la rama
  //   else se monta. La animación de entrada la dispara Effect 2.
  useEffect(() => {
    if (step === 'preview') {
      setPanelOpen(false);
      const id = window.setTimeout(() => setRenderedStep('preview'), 300);
      return () => window.clearTimeout(id);
    }
    setRenderedStep(step);
  }, [step]);

  // Effect 2: animación de entrada del panel cuando se viene de
  // 'preview'. useLayoutEffect garantiza que corre síncrono justo
  // después del commit que monta el panel — así setPanelOpen(false)
  // se aplica antes del paint, y luego el rAF dispara setPanelOpen(true)
  // en el siguiente frame para que CSS transition detecte el cambio
  // 0 → 55dvh. Sin el useLayoutEffect, React podía batchear los dos
  // setStates en un único commit y la animación se "saltaba".
  const prevRenderedStepRef = useRef(renderedStep);
  useLayoutEffect(() => {
    const prev = prevRenderedStepRef.current;
    prevRenderedStepRef.current = renderedStep;
    if (renderedStep === 'preview') return;
    // Solo animamos cuando venimos de preview (mount fresco del panel).
    // Transiciones entre non-preview steps (choose-product → configure)
    // no resetean panelOpen — el panel sigue abierto y solo cambia
    // max-height de 55dvh a 78dvh por la transición CSS existente.
    if (prev !== 'preview') return;
    setPanelOpen(false);
    let rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => setPanelOpen(true));
    });
    return () => cancelAnimationFrame(rafId);
  }, [renderedStep]);

  // Detección de dirección del cambio de step para elegir la animación
  // del contenido al cambiar entre steps non-preview (choose-product ↔
  // configure ↔ upload-product, etc.). Adelante = el contenido nuevo
  // entra de abajo (`animate-mode-swap`); atrás = de arriba
  // (`animate-mode-swap-back`), dando la sensación de "desandar" el
  // movimiento del panel. La transición preview↔step no usa esto — ahí
  // el panel mismo desliza con max-height.
  const stepOrder = (s: typeof step): number => {
    switch (s) {
      case 'preview':
        return 0;
      case 'choose-product':
        return 1;
      case 'pick-product':
      case 'upload-product':
      case 'select-vistas':
        return 2;
      case 'configure':
        return 3;
      default:
        return 0;
    }
  };
  const prevStepForAnimRef = useRef(step);
  const isBackStepTransition =
    stepOrder(step) < stepOrder(prevStepForAnimRef.current);
  useEffect(() => {
    prevStepForAnimRef.current = step;
  }, [step]);
  const swapAnimClass = isBackStepTransition
    ? 'animate-mode-swap-back'
    : 'animate-mode-swap';

  // Sincronizar el scroll con `previewIndex` cada vez que entramos al
  // step preview (mount inicial + volver desde choose-product).
  // Usamos `renderedStep` (no `step`) porque la transición OUT tiene
  // delay: el scroll container del preview branch recién se monta
  // cuando `renderedStep` pasa a 'preview', después de que el panel
  // terminó de animar.
  useEffect(() => {
    if (renderedStep !== 'preview') return;
    const el = mobilePreviewScrollRef.current;
    if (!el || el.clientHeight <= 0) return;
    el.scrollTop = previewIndex * el.clientHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedStep]);

  // Si el user vino del flow multi-select, resolvemos los preset objects
  // a partir de los ids (lookup en previewList) — se usan para mostrar
  // un carrusel en vez de una sola imagen en los steps post-preview.
  const multiPresets = React.useMemo<PresetV2[]>(() => {
    if (multiPresetIds.length < 2) return [];
    return multiPresetIds
      .map((id) => previewList.find((p) => p.id === id))
      .filter((p): p is PresetV2 => !!p);
  }, [multiPresetIds, previewList]);
  const isMultiPreset = multiPresets.length > 1;
  const desktopPreviewPreset = previewList[previewIndex] || preset;

  const canPrev = previewIndex > 0;
  const canNext = previewIndex < previewList.length - 1;

  // Multi-select: aplica a kind 'template' siempre y a kind 'design'
  // cuando la pose tiene UNA sola persona. Las poses multi-persona no
  // admiten multi-select porque el backend solo soporta una pose
  // multi-persona por job (mezclar dos poses con N personas no tiene
  // semántica clara). En multiMode el botón "Utilizar" se reemplaza
  // por "Utilizar seleccionadas (N)" y los cards muestran un check
  // overlay. Tap en card toggle la selección en vez de utilizar.
  //
  // `canMultiSelect` ahora depende del PRESET ACTUAL (currentPreset al
  // navegar en preview), no del preset inicial — para que al swipear
  // entre poses de e-commerce, una pose 2-persona oculte el botón
  // mientras una 1-persona lo muestre.
  const isPresetMultiSelectable = (p: PresetV2 | null | undefined): boolean => {
    if (!p) return false;
    if (p.kind === 'template') return true;
    if (p.kind === 'design') return (p.personCount ?? 1) === 1;
    return false;
  };
  const [multiMode, setMultiMode] = useState<boolean>(false);
  const [multiSelectedIds, setMultiSelectedIds] = useState<string[]>([]);
  const isMultiSelected = (id: string) => multiSelectedIds.includes(id);
  const toggleMultiId = (id: string) => {
    setMultiSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  // Tap en "Seleccionar varias" arranca el modo y agrega el preset
  // actualmente visible (el que el user está mirando). Sino el user ve
  // checks vacíos y no entiende qué pasa.
  const enterMultiMode = (currentId: string) => {
    setMultiMode(true);
    setMultiSelectedIds([currentId]);
  };
  // Salir de multi mode resetea la selección. Lo llamamos cuando el user
  // toca cancelar o cuando deselect lleva a N=0.
  const exitMultiMode = () => {
    setMultiMode(false);
    setMultiSelectedIds([]);
  };
  const multiSelectedPresets = multiSelectedIds
    .map((id) => previewList.find((p) => p.id === id))
    .filter((p): p is PresetV2 => !!p);
  const handleConfirmMulti = () => {
    if (multiSelectedPresets.length === 0) return;
    onUtilizarMultiple(multiSelectedPresets);
  };
  // Si el user salió del step 'preview' o cambió de colección (previewList
  // distinto), reseteamos el modo para no arrastrar selección stale.
  useEffect(() => {
    if (step !== 'preview') {
      setMultiMode(false);
      setMultiSelectedIds([]);
    }
  }, [step]);

  // Wheel/trackpad navigation en el carousel desktop. Mantenemos un lock
  // corto para que un fling continuo no skippee 5 plantillas de una.
  // Usamos addEventListener con passive: false para poder preventDefault y
  // así el browser no haga back/forward gesture en mac.
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const wheelLockRef = useRef<boolean>(false);
  useEffect(() => {
    if (step !== 'preview') return;
    const el = carouselRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Si el trackpad da deltaX (swipe horizontal) lo preferimos. Sino
      // caemos a deltaY (mouse wheel) — así también con scroll vertical
      // se puede navegar de plantilla, que es el feedback que pidió el user.
      const horizIntent = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const delta = horizIntent ? e.deltaX : e.deltaY;
      if (Math.abs(delta) < 12) return;
      e.preventDefault();
      if (wheelLockRef.current) return;
      if (delta > 0) {
        setPreviewIndex((i) =>
          i < previewList.length - 1 ? i + 1 : i,
        );
      } else {
        setPreviewIndex((i) => (i > 0 ? i - 1 : i));
      }
      wheelLockRef.current = true;
      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 450);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [step, previewList.length]);
  // El step content lo compartimos entre mobile y desktop. Para el step
  // 'configure', PASAMOS un flag `useStickyGenerate` para que en desktop
  // el botón Generar quede pegado al bottom del panel (mt-auto). En
  // mobile el panel es un bottom sheet limitado a 55dvh con scroll
  // interno — no aplica el sticky bottom.
  const renderStep = (variant: 'mobile' | 'desktop') => {
    // En mobile, los steps 'pick-product' y 'upload-product' viven en una
    // pantalla full-screen aparte (portal sobre la top bar). El preset
    // sheet inline debajo se queda con el contenido de choose-product como
    // fallback (es lo que el usuario verá cuando cierre esa pantalla).
    const effectiveStep =
      variant === 'mobile' &&
      (step === 'pick-product' || step === 'upload-product')
        ? 'choose-product'
        : step;

    return effectiveStep === 'choose-product' ? (
      <ChooseProductPanel
        onShowProducts={onShowProducts}
        onStartUpload={onStartUpload}
        presetKind={preset.kind}
      />
    ) : effectiveStep === 'pick-product' ? (
      <PickProductPanel
        variant={variant}
        onConfirm={onConfirmProducts}
        onBack={onBackToChoose}
        initialSelectedIds={selectedProducts.map((p) => p.id)}
        tiposProductoSugeridos={preset.tiposProductoSugeridos}
        rubro={rubro}
      />
    ) : effectiveStep === 'upload-product' ? (
      <UploadProductPanel
        variant={variant}
        files={pendingProductFiles}
        name={pendingProductName}
        onNameChange={onPendingNameChange}
        onAddMore={onAddMoreFiles}
        onRemoveFile={onRemovePendingFile}
        onCancel={onCancelUpload}
        onConfirm={onConfirmUpload}
        uploading={uploadingProduct}
        error={uploadError}
      />
    ) : (
      <ConfigurePanel
        variant={variant}
        products={selectedProducts}
        onAddProducts={onAddProducts}
        kind={
          // PresetDetailSheet nunca renderiza ConfigurePanel para
          // `asistente` — el padre conmuta a AsistenteMarketingSheet.
          // El cast es defensivo para el typecheck.
          (preset.kind || 'template') as Exclude<
            NonNullable<PresetV2['kind']>,
            'asistente'
          >
        }
        acceptsModelo={
          !preset.inputsAceptados || preset.inputsAceptados.includes('modelo')
        }
        modelSelection={modelSelection}
        fondoVal={fondoVal}
        formatoVal={formatoVal}
        otraConfig={otraConfig}
        stickyGenerate={variant === 'desktop' || isDesignFlow}
        // En desktop sólo design-modelo usa el botón Generar flotante
        // arriba a la derecha — ahí escondemos el sticky del panel.
        // design-auto vuelve al layout estándar (Generar sticky al pie
        // del card de Opciones, como en las plantillas).
        hideGenerateButton={variant === 'desktop' && isFullScreenConfigure}
        creditCost={creditCost}
        lockSlotCount={lockSlotCount}
        onClearProducts={onClearProducts}
        onModelChange={onModelChange}
        modelSelectionsExtra={modelSelectionsExtra}
        onModelChangeExtras={onModelChangeExtras}
        multiModelEnabled={multiModelEnabled}
        onMultiModelEnabledChange={onMultiModelEnabledChange}
        onOpenModelPicker={onOpenModelPicker}
        onOpenModelSheet={onOpenModelSheet}
        onOpenModelSheetForMultiSlot={onOpenModelSheetForMultiSlot}
        multiProductsPerSlot={multiProductsPerSlot}
        onTapSlotProducts={onTapSlotProducts}
        onOpenFondoSheet={onOpenFondoSheet}
        onFondoChange={onFondoChange}
        onFormatoChange={onFormatoChange}
        onOpenFormatPicker={onOpenFormatPicker}
        onOtraChange={onOtraChange}
        onGenerate={onGenerate}
        autoImageCount={autoImageCount}
        onAutoImageCountChange={onAutoImageCountChange}
        autoImageCountMax={autoImageCountMax}
        userModels={userModels}
        userAuthId={userAuthId}
        userCompanyId={userCompanyId}
        onModelCreated={onModelCreated}
        allPoses={allPoses}
        poseIds={poseIds}
        onPoseIdsChange={onPoseIdsChange}
        onOpenPosePicker={onOpenPosePicker}
        bibliotecaFondos={bibliotecaFondos}
        fondosGuardados={fondosGuardados}
        fondoSelection={fondoSelection}
        onFondoSelectionChange={onFondoSelectionChange}
        onOpenFondoPicker={onOpenFondoPicker}
        fondoView={fondoView}
        onFondoViewChange={onFondoViewChange}
        onFondoSaved={onFondoSaved}
        videoModeEnabled={isVideoModeActive}
        videoDuration={videoDuration}
        videoMultiScene={videoMultiScene}
        videoCreditCostPerImage={videoCreditCostPerImage}
        onOpenVideoConfig={onOpenVideoConfig}
        anuncioTitulo={anuncioTitulo}
        onAnuncioTituloChange={onAnuncioTituloChange}
        anuncioDescripcion={anuncioDescripcion}
        onAnuncioDescripcionChange={onAnuncioDescripcionChange}
        anuncioCta={anuncioCta}
        onAnuncioCtaChange={onAnuncioCtaChange}
        anuncioColors={anuncioColors}
        onAnuncioColorsChange={onAnuncioColorsChange}
        anuncioBrandColors={anuncioBrandColors}
      />
    );
  };

  // El mobile preset sheet se renderiza INLINE (sin portal) — choose-
  // product / configure se quedan debajo de la top bar de la página
  // como cualquier bottom sheet inline. SOLO el pick-product es full-
  // screen sobre la top bar (ese se rendiriza por separado más abajo
  // vía createPortal).
  return (
    <>
      {/* Vista picker fullscreen para el step `select-vistas` del modo
          catalogo-ia. Se monta sobre todo lo demás cuando aplica.
          Renderizamos 2 instancias responsivas — la variant define el
          layout (mobile = full inset, desktop = left: sidebarWidth). */}
      <div className="md:hidden">
        <VistaPickerFullscreen
          open={step === 'select-vistas'}
          variant="mobile"
          sidebarWidth={sidebarWidth}
          allPoses={allPoses}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          onChange={onCatalogoVistasChange}
          onBack={onBackToChoose}
          onNext={onConfirmCatalogoVistas}
        />
      </div>
      <div className="hidden md:block">
        <VistaPickerFullscreen
          open={step === 'select-vistas'}
          variant="desktop"
          sidebarWidth={sidebarWidth}
          allPoses={allPoses}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          onChange={onCatalogoVistasChange}
          onBack={onBackToChoose}
          onNext={onConfirmCatalogoVistas}
        />
      </div>

      {/* Cards review fullscreen — paso final del flow catalogo-ia,
          reemplaza al ConfigurePanel cuando el preset es catalogo-ia
          y el step es 'configure'. Una instancia por breakpoint, igual
          que el VistaPicker. */}
      <div className="md:hidden">
        <CatalogoCardsFullscreen
          open={
            step === 'configure' && preset.kind === 'catalogo-ia'
          }
          variant="mobile"
          sidebarWidth={sidebarWidth}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          onBack={onBackFromCatalogoConfigure}
          onGenerate={onGenerateCatalog}
          userCompanyId={userCompanyId}
          brandId={brandId}
          rubro={rubro}
          tiposProductoSugeridos={preset.tiposProductoSugeridos}
          onProductsRefreshed={onProductsRefreshed}
          fondoSelection={fondoSelection}
          modelSelection={modelSelection}
          onOpenFondo={() => onOpenFondoSheet('list')}
          onOpenModel={() => onOpenModelSheet('list')}
        />
      </div>
      <div className="hidden md:block">
        <CatalogoCardsFullscreen
          open={
            step === 'configure' && preset.kind === 'catalogo-ia'
          }
          variant="desktop"
          sidebarWidth={sidebarWidth}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          onBack={onBackFromCatalogoConfigure}
          onGenerate={onGenerateCatalog}
          userCompanyId={userCompanyId}
          brandId={brandId}
          rubro={rubro}
          tiposProductoSugeridos={preset.tiposProductoSugeridos}
          onProductsRefreshed={onProductsRefreshed}
          fondoSelection={fondoSelection}
          modelSelection={modelSelection}
          onOpenFondo={() => onOpenFondoSheet('list')}
          onOpenModel={() => onOpenModelSheet('list')}
        />
      </div>
      <div
        className={`md:hidden fixed inset-0 z-[100] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-[101] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out rounded-none ${
          animated ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          // El sheet siempre llega hasta arriba (100dvh) para que el
          // header transparente flote encima sin franja del home
          // detrás. Los distintos steps acomodan su contenido (imagen
          // + panel) usando flex / max-height adentro. NO aplicamos
          // padding-bottom acá — en PWA standalone con viewport-fit=
          // cover queremos que la imagen full-bleed llegue hasta el
          // borde físico. El CTA flotante "Generar con mi producto"
          // ya maneja su propio `env(safe-area-inset-bottom)` para no
          // quedar debajo del home indicator.
          height: '100dvh',
        }}
      >

        {renderedStep === 'preview' ? (
          // ── Preview: scroll vertical con cards una por viewport ──────
          // Cada card es `min-h-full` (toma toda la altura del sheet) y
          // tiene `snap-start`. El onScroll mantiene `previewIndex`
          // sincronizado con la card visible → el footer fijo (afuera
          // del scroll) usa siempre el preset correcto al tapear
          // "Generar con mi producto".
          //
          // En multi-mode, mostramos un strip sticky abajo con las
          // plantillas seleccionadas (fuera del scroll, sibling del
          // mismo contenedor flex-col).
          <div className="flex-1 min-h-0 flex flex-col">
          <div
            ref={mobilePreviewScrollRef}
            className="flex-1 overflow-y-auto snap-y snap-mandatory"
            style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.clientHeight <= 0) return;
              const idx = Math.round(el.scrollTop / el.clientHeight);
              if (idx !== previewIndex && idx >= 0 && idx < previewList.length) {
                setPreviewIndex(idx);
              }
            }}
          >
            {previewList.map((p) => (
              <div
                key={p.id}
                // Cada card ocupa todo el viewport del sheet. La imagen
                // hace full-bleed (object-cover); el footer/check fijos
                // viven afuera del scroll para no moverse al scrollear.
                className="snap-start min-h-full relative overflow-hidden"
              >
                {/* Imagen full-bleed. El button entero detecta tap solo en
                    multi-mode (sino el scroll es la única interacción). */}
                <button
                  type="button"
                  onClick={() => multiMode && toggleMultiId(p.id)}
                  disabled={!multiMode}
                  aria-label={
                    multiMode
                      ? isMultiSelected(p.id)
                        ? `Quitar ${p.title}`
                        : `Seleccionar ${p.title}`
                      : undefined
                  }
                  className="absolute inset-0 disabled:cursor-default"
                >
                  {p.outputType === 'video' ? (
                    <video
                      src={p.imageUrlFull || p.imageUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      poster={p.imageUrl}
                      className="absolute inset-0 w-full h-full object-cover bg-neutral-100"
                    />
                  ) : (
                    <img
                      src={p.imageUrlFull || p.imageUrl}
                      alt={p.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover bg-neutral-100"
                    />
                  )}
                </button>
              </div>
            ))}
          </div>
          {/* ───── Overlays fijos (no se mueven con el scroll) ───── */}
          {/* Gradient bottom para legibilidad del footer. */}
          <div
            className="absolute bottom-0 inset-x-0 h-44 bg-gradient-to-t from-black/65 via-black/25 to-transparent pointer-events-none z-10"
          />
          {/* Footer fijo. Comportamiento del botón:
              • single mode → "Generar con mi producto" (acción directa).
              • multi-mode + card visible NO seleccionada → "Seleccionar"
                (tap añade la card al set).
              • multi-mode + card visible seleccionada → "Generar (N)"
                (tap dispara handleConfirmMulti) + un pill "Seleccionada"
                arriba del botón como indicador visual.
              Se desliza ~88px arriba cuando el multi-strip aparece. */}
          {(() => {
            const currentPreset = previewList[previewIndex];
            const currentSelected =
              !!currentPreset && isMultiSelected(currentPreset.id);
            return (
              <div
                className="absolute inset-x-5 z-20 transition-[bottom] duration-300 ease-out"
                style={{
                  bottom:
                    multiMode && multiSelectedPresets.length > 0
                      ? 'calc(env(safe-area-inset-bottom, 0px) + 112px)'
                      : 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                }}
              >
                <div className="flex items-center gap-2.5">
                  {isPresetMultiSelectable(currentPreset) && !multiMode && currentPreset && (
                    <button
                      type="button"
                      onClick={() => enterMultiMode(currentPreset.id)}
                      aria-label="Seleccionar varias"
                      className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/95 backdrop-blur-sm border border-white/40 text-brand-black/85 shadow-sm transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (!currentPreset) return;
                      if (!multiMode) {
                        onUtilizarPreset(currentPreset);
                        return;
                      }
                      // Multi-mode: la card visible decide la acción.
                      // Sin seleccionar → añadirla. Ya seleccionada →
                      // confirmar generación con todo el set.
                      // Si la card actual NO es multi-selectable (e.g.
                      // pose multi-persona) y aún no está en el set,
                      // no la sumamos: si ya hay seleccionadas confirma,
                      // sino utiliza ese preset como single.
                      const canAddCurrent =
                        isPresetMultiSelectable(currentPreset);
                      if (!canAddCurrent && !currentSelected) {
                        if (multiSelectedPresets.length > 0) {
                          handleConfirmMulti();
                        } else {
                          onUtilizarPreset(currentPreset);
                        }
                        return;
                      }
                      if (currentSelected) {
                        handleConfirmMulti();
                      } else {
                        toggleMultiId(currentPreset.id);
                      }
                    }}
                    disabled={multiMode && !currentPreset}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-md transition-colors"
                  >
                    {/* Sparkles para "Generar"; Plus para "Seleccionar"
                        — el ícono refleja la acción (añadir vs generar). */}
                    {multiMode &&
                    !currentSelected &&
                    isPresetMultiSelectable(currentPreset) ? (
                      <Plus className="w-5 h-5" strokeWidth={2.5} />
                    ) : (
                      <Sparkles className="w-5 h-5" />
                    )}
                    <span>
                      {!multiMode
                        ? 'Generar con mi producto'
                        : currentSelected
                          ? 'Generar'
                          : isPresetMultiSelectable(currentPreset)
                            ? 'Seleccionar'
                            : multiSelectedPresets.length > 0
                              ? 'Generar'
                              : 'Generar con mi producto'}
                    </span>
                    {/* Badge circular blanco con el conteo — solo cuando
                        el label efectivo es "Generar" en multi-mode con
                        N items. */}
                    {multiMode &&
                      multiSelectedIds.length > 0 &&
                      (currentSelected ||
                        !isPresetMultiSelectable(currentPreset)) && (
                        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-white text-brand-primary text-[13px] font-bold leading-none tabular-nums">
                          {multiSelectedIds.length}
                        </span>
                      )}
                  </button>
                </div>
              </div>
            );
          })()}
          {/* Strip flotante con las seleccionadas — overlay absolute para
              no empujar la UI ni crear scroll extra. Aparece solo cuando
              hay al menos una seleccionada, con un fade+slide-up sutil
              (animate-mode-swap, 200ms) al primer monte. */}
          {multiMode && multiSelectedPresets.length > 0 && (
            <div
              className="absolute left-3 right-3 z-30 pointer-events-none animate-mode-swap"
              style={{
                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="pointer-events-auto">
                <MultiSelectedStrip
                  selected={multiSelectedPresets}
                  onRemove={toggleMultiId}
                  onCancel={exitMultiMode}
                />
              </div>
            </div>
          )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Imagen del preset arriba — en 'design-modelo' se oculta
                completamente al entrar a 'configure' (el modo tiene
                muchas opciones y la imagen no aporta). En design-modelo
                non-configure usamos fondo blanco + object-contain con
                padding (la modelo_gia.png se ve más prolija así, no
                full-bleed como en templates de moda). */}
            <div
              className={`transition-all duration-300 ease-out overflow-hidden ${
                isDesignFlow && step === 'configure'
                  ? 'h-0 opacity-0'
                  : isDesignFlow || preset.kind === 'catalogo-ia'
                    ? 'bg-white flex-1 min-h-0 flex items-center justify-center p-6 opacity-100'
                    : 'relative bg-neutral-100 flex-1 min-h-0 opacity-100'
              }`}
            >
              {isDesignFlow || preset.kind === 'catalogo-ia' ? (
                <img
                  src={preset.imageUrlFull || preset.imageUrl}
                  alt={preset.title}
                  // `max-h-full` mantiene el contain de design-modelo/auto
                  // (landscape). Para catalogo-ia (portrait) el max-w
                  // limita el ancho — y como hay `flex items-center`,
                  // queda visualmente similar al resto.
                  className="max-w-[300px] max-h-full w-full h-auto object-contain"
                />
              ) : isMultiPreset ? (
                <MultiPresetCarousel presets={multiPresets} fit="cover" />
              ) : preset.outputType === 'video' ? (
                <video
                  src={preset.imageUrlFull || preset.imageUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster={preset.imageUrl}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <img
                  src={preset.imageUrlFull || preset.imageUrl}
                  alt={preset.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
            </div>

            {/* La altura del panel adentro del preset sheet cambia según
                el step:
                • choose-product               → 55dvh (panel chico)
                • upload-product · configure   → 78dvh (más espacio para
                                                   previews + form / opts)
                • design-modelo                → ocupa todo el sheet
                pick-product NO afecta este panel — ese caso se maneja con
                un sheet aparte (portal) sobre la top bar. */}
            <div
              // El panel arranca con max-height: 0 (panelOpen=false en
              // el primer render post-mount) y en el siguiente frame
              // se anima a su valor final. Esto cambia el flex-space
              // disponible para la imagen (flex-1 arriba) → la imagen
              // anima su altura sin script extra. design-modelo +
              // configure usa flex-1 sin max-height (panel ocupa
              // todo).
              className={`relative z-10 bg-white flex flex-col overflow-hidden transition-all duration-300 ease-out ${
                isDesignFlow && step === 'configure'
                  ? 'flex-1 min-h-0'
                  : 'shrink-0 -mt-6 rounded-t-3xl'
              }`}
              style={{
                maxHeight:
                  isDesignFlow && step === 'configure'
                    ? undefined
                    : !panelOpen
                      ? '0px'
                      : step === 'configure' || step === 'upload-product'
                        ? '78dvh'
                        : '55dvh',
                transition: 'max-height 300ms ease-out',
              }}
            >
              <div
                key={step}
                // En design-modelo + configure el panel ocupa todo el
                // sheet (sin imagen del preset arriba), así que el
                // contenido arranca pegado al borde superior y los
                // botones flotantes (back / menu, del header
                // transparente) tapan el primer chip. Agregamos
                // padding superior + safe-area-top en ese caso para
                // que el primer elemento quede debajo de los botones.
                //
                // pb-6 default — pero en design-flow + configure lo
                // sacamos porque el ConfigurePanel renderiza un Generar
                // sticky con su propio padding interno; cualquier pb
                // del wrapper deja un gap visible entre el botón y el
                // borde inferior.
                className={`${swapAnimClass} flex-1 overflow-y-auto px-5 ${
                  isDesignFlow && step === 'configure' ? '' : 'pb-6'
                }`}
                style={{
                  paddingTop:
                    isDesignFlow && step === 'configure'
                      ? 'calc(env(safe-area-inset-top, 0px) + 5rem)'
                      : '1.75rem', // pt-7 default
                }}
              >
                {renderStep('mobile')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Mobile-only: pick-product como PANTALLA completa ── */}
      {/* Cuando step === 'pick-product' en mobile, renderizamos una pantalla
          full-screen (no un bottom sheet) a través de portal a document.body.
          Va POR DEBAJO del chrome del Sidebar (z-50) — z-[35] queda encima
          del contenido de la página (shell z-30) pero deja visibles y
          clickeables los botones de navegación flotantes (volver + menú). Por
          eso el contenido arranca debajo de la top bar (padding-top =
          safe-area + 3.5rem, la altura del header mobile) y ocultamos el link
          "Volver" interno del panel (`hideBack`): el back lo maneja la top bar
          vía `mobileBackOverride`. Usamos la variante `desktop` para tener el
          mismo layout que en escritorio: header + grilla + botón grande de
          Confirmar al pie. La animación `page-slide-in-right` le da el feel de
          "navegar a otra pantalla". */}
      {step === 'pick-product' &&
        ReactDOM.createPortal(
          <div
            className="md:hidden fixed inset-0 z-[35] bg-white flex flex-col page-slide-in-right"
            style={{
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <PickProductPanel
              variant="desktop"
              hideBack
              onConfirm={onConfirmProducts}
              onBack={onBackToChoose}
              initialSelectedIds={selectedProducts.map((p) => p.id)}
              tiposProductoSugeridos={preset.tiposProductoSugeridos}
              rubro={rubro}
            />
          </div>,
          document.body,
        )}

      {/* ── Mobile-only: upload-product como PANTALLA completa ── */}
      {/* Mismo patrón que pick-product: pantalla full-screen por debajo del
          chrome (z-[35] < z-50), contenido debajo de la top bar y sin link
          "Volver" interno (el back lo da la top bar vía mobileBackOverride,
          que en upload-product vuelve a choose-product). Variante `desktop`
          para el layout header + scroll + footer con Confirmar. */}
      {step === 'upload-product' &&
        ReactDOM.createPortal(
          <div
            className="md:hidden fixed inset-0 z-[35] bg-white flex flex-col page-slide-in-right"
            style={{
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <UploadProductPanel
              variant="desktop"
              hideBack
              files={pendingProductFiles}
              name={pendingProductName}
              onNameChange={onPendingNameChange}
              onAddMore={onAddMoreFiles}
              onRemoveFile={onRemovePendingFile}
              onCancel={onCancelUpload}
              onConfirm={onConfirmUpload}
              uploading={uploadingProduct}
              error={uploadError}
            />
          </div>,
          document.body,
        )}

      {/* ── Desktop: layout tipo "página" con dos cards ─────────── */}
      {/* En vez del overlay edge-to-edge, simulamos una página normal:
          fondo gris del shell (igual a `EditarImagen`), padding alrededor,
          y dos cards blancas (imagen + opciones) flotando una al lado de
          la otra. El botón "Volver" vive en un header arriba de las
          cards, no dentro de ellas, para que ambos se sientan como
          contenedores parejos. */}
      <div
        className={`hidden md:flex fixed inset-y-0 right-0 z-[100] bg-[#EFF0F5] flex-col transition-all duration-300 ease-out ${
          animated ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.99]'
        } ${
          // En design-modelo configure el contenido es alto (Modelo +
          // Pose + Fondo + Formato + Otra config) — dejamos que la
          // ventana sea la scrolleable en vez del aside interno, y el
          // header con Volver + Generar queda sticky encima. design-
          // auto tiene menos campos y vuelve al layout estándar.
          isFullScreenConfigure && step === 'configure'
            ? 'overflow-y-auto scrollbar-hide'
            : ''
        }`}
        style={{ left: sidebarWidth }}
      >
        {/* Header con Volver + Generar. En design-modelo configure es
            sticky transparente para que ambos botones queden pegados
            arriba del scroll sin tapar las cards (cada botón lleva su
            propio fondo opaco/pill para ser legible). `pointer-events-
            none` en el container deja que los clicks pasen a las cards
            de atrás, y `pointer-events-auto` en los botones los re-
            habilita. Otros steps: header normal `shrink-0` con sólo
            Volver. */}
        <header
          className={`relative px-6 pt-5 pb-3 ${
            isFullScreenConfigure && step === 'configure'
              ? 'sticky top-0 z-[105] bg-transparent pointer-events-none flex items-center justify-between'
              : 'shrink-0'
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity pointer-events-auto ${
              isFullScreenConfigure && step === 'configure'
                ? 'px-3 py-1.5 rounded-full bg-white/85 backdrop-blur-md shadow-sm ring-1 ring-black/[0.04]'
                : ''
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
          {/* Toggle Imagen/Video centrado en el header — solo en el step
              'preview', cuando hay carousel de plantillas para elegir. En
              otros steps (choose-product, configure, etc.) ya se decidió
              qué se va a generar, así que el toggle no aplica. */}
          {step === 'preview' && (
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 inline-grid grid-cols-2 p-0.5 rounded-full bg-white shadow-sm ring-1 ring-black/[0.06] pointer-events-auto"
            >
              <div
                aria-hidden
                className="absolute top-0.5 bottom-0.5 left-0.5 right-1/2 rounded-full bg-brand-primary transition-transform duration-300 ease-out"
                style={{
                  transform:
                    previewMediaTab === 'imagen'
                      ? 'translateX(0)'
                      : 'translateX(100%)',
                }}
              />
              <button
                type="button"
                onClick={() => onPreviewMediaTabChange('imagen')}
                className={`relative z-10 inline-flex items-center justify-center px-5 h-8 rounded-full text-[14px] font-semibold transition-colors duration-300 ${
                  previewMediaTab === 'imagen'
                    ? 'text-white'
                    : 'text-brand-black/65'
                }`}
              >
                Imagen
              </button>
              <button
                type="button"
                onClick={() => onPreviewMediaTabChange('video')}
                className={`relative z-10 inline-flex items-center justify-center px-5 h-8 rounded-full text-[14px] font-semibold transition-colors duration-300 ${
                  previewMediaTab === 'video'
                    ? 'text-white'
                    : 'text-brand-black/65'
                }`}
              >
                Video
              </button>
            </div>
          )}
          {/* Generar flotante (pill arriba a la derecha) — solo para
              design-modelo. design-auto renderiza el Generar pegado al
              bottom del card de Opciones vía ConfigurePanel. */}
          {isFullScreenConfigure && step === 'configure' && (
            <button
              type="button"
              onClick={onGenerate}
              className="inline-flex items-center gap-2 py-2.5 pl-5 pr-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-md transition-colors pointer-events-auto"
            >
              <Sparkles className="w-4 h-4" />
              <span>Generar</span>
              {creditCost != null && creditCost > 0 && (
                <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
                  <img
                    src="/images/token_icon.png"
                    alt=""
                    aria-hidden="true"
                    className="w-3.5 h-3.5 object-contain"
                  />
                  {creditCost}
                </span>
              )}
            </button>
          )}
        </header>

        {step === 'preview' ? (
          // ── Desktop preview: carousel transicional ────────────────────
          // Cards absolute positioned con transform basado en (i - index).
          // Flechas a los costados de la imagen central (absolute dentro
          // del carousel area, vertical centerd). Soporte de wheel/swipe
          // horizontal para navegar — listener attached via useEffect.
          //
          // El container es `relative` para que el MultiSelectedStrip
          // pueda flotar absoluto en el bottom sin empujar la UI. Usamos
          // pb-28 SIEMPRE (no condicional) — con justify-center, ese
          // padding sube la columna a una posición donde queda lugar
          // para el strip flotante, y al aparecer/desaparecer el strip
          // la UI no se mueve.
          <div className="flex-1 px-6 pb-20 flex flex-col gap-5 min-h-0 justify-center items-center relative">
            {/* Toggle Imagen/Video vive ahora en el `<header>` arriba de
                este container — para mantener la altura del carousel +
                title intactas y reusar el espacio que ya queda libre
                arriba al lado de "Volver". */}
            <div
              ref={carouselRef}
              className="relative w-full flex-1 min-h-0 max-h-[72dvh] flex items-center justify-center overflow-hidden"
            >
              {previewList.map((p, i) => {
                const rel = i - previewIndex;
                if (Math.abs(rel) > 2) return null;
                const isCenter = rel === 0;
                // ±1 mantiene los valores anteriores. ±2 va al lado de
                // ±1 con MISMO tamaño (scale 0.42) y separación similar
                // a la que ±1 tiene con la central. Pueden quedar
                // parcialmente clippeadas por el overflow-hidden del
                // container — eso da el efecto "peek" sin overflow hacia
                // la sidebar.
                const absRel = Math.abs(rel);
                const translateX =
                  Math.sign(rel) * (absRel === 2 ? 712 : absRel === 1 ? 470 : 0);
                const scale = absRel === 0 ? 1 : 0.42;
                const opacity = absRel === 2 ? 0.4 : absRel === 1 ? 0.5 : 1;
                // Click en cualquier card LATERAL = saltar a esa (la
                // pone como centro). Click en la CENTRAL = utilizar en
                // single mode, o toggle selección en multi-mode.
                const onClick = !isCenter
                  ? () => setPreviewIndex(i)
                  : multiMode
                    ? () => toggleMultiId(p.id)
                    : () => onUtilizarPreset(p);
                const ariaLabel = !isCenter
                  ? `Ir a ${p.title}`
                  : multiMode
                    ? `${isMultiSelected(p.id) ? 'Quitar' : 'Seleccionar'} ${p.title}`
                    : `Utilizar ${p.title}`;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={onClick}
                    aria-label={ariaLabel}
                    className="absolute h-full max-h-[72dvh] aspect-[3/4] rounded-2xl overflow-hidden bg-neutral-100 ring-1 ring-black/[0.04] shadow-md transition-all duration-500 ease-out"
                    style={{
                      transform: `translateX(${translateX}px) scale(${scale})`,
                      opacity,
                      zIndex: 10 - absRel,
                      // Todas las visibles son clickeables — incluso las
                      // de rel=2 que asoman parcialmente, así el user
                      // puede tocarlas para saltar a esa plantilla.
                      pointerEvents: 'auto',
                      cursor: 'pointer',
                    }}
                  >
                    {p.outputType === 'video' ? (
                      <video
                        src={isCenter ? p.imageUrlFull || p.imageUrl : p.imageUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        poster={p.imageUrl}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        // Centro usa full (se ve grande); laterales usan
                        // opt porque están escaladas a ~0.42 y el opt
                        // alcanza para esa resolución.
                        src={isCenter ? p.imageUrlFull || p.imageUrl : p.imageUrl}
                        alt={p.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {multiMode && (
                      <span
                        className={`absolute top-3 right-3 w-8 h-8 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white transition-colors ${
                          isMultiSelected(p.id)
                            ? 'bg-brand-primary text-white'
                            : 'bg-white/85 backdrop-blur-sm text-transparent'
                        }`}
                      >
                        <Check className="w-4 h-4" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}

              {/* Flechas — absolute al costado de la imagen central. La
                  central tiene 580px de ancho DOM (aspect 3/4 scaled 1),
                  centrada en el container. Posicionamos las flechas a
                  ~340px del centro para que queden a los lados de la
                  imagen, encima de las laterales (z-20). */}
              {/* Vertical center sin transform: inset-y-0 + my-auto.
                  Antes usábamos `top-1/2 -translate-y-1/2` y el transform
                  parece que en algunos browsers se "resetea" en :active
                  o interfiere con el hit testing (el user reportaba que
                  el botón "se movía para abajo" al presionarlo). Sin
                  transform, la posición es 100% estática.

                  Wrapper más grande con padding para hit area cómoda;
                  visualmente el círculo es 44×44 pero el target táctil
                  es ~56×56. */}
              <button
                type="button"
                onClick={() => canPrev && setPreviewIndex((i) => i - 1)}
                disabled={!canPrev}
                aria-label="Anterior"
                style={{ left: 'calc(50% - 346px)' }}
                className="absolute inset-y-0 my-auto h-11 z-20 inline-flex items-center justify-center p-1.5 box-content rounded-full hover:bg-neutral-50/0 disabled:cursor-not-allowed transition-colors"
              >
                <span
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-brand-black/70 shadow-md ring-1 ring-black/[0.04] transition-colors ${
                    canPrev ? 'hover:bg-neutral-50' : 'opacity-30'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </span>
              </button>
              <button
                type="button"
                onClick={() => canNext && setPreviewIndex((i) => i + 1)}
                disabled={!canNext}
                aria-label="Siguiente"
                style={{ right: 'calc(50% - 346px)' }}
                className="absolute inset-y-0 my-auto h-11 z-20 inline-flex items-center justify-center p-1.5 box-content rounded-full disabled:cursor-not-allowed transition-colors"
              >
                <span
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-brand-black/70 shadow-md ring-1 ring-black/[0.04] transition-colors ${
                    canNext ? 'hover:bg-neutral-50' : 'opacity-30'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </span>
              </button>
            </div>

            {/* Title row debajo, sin flechas ahora (viven arriba al lado
                de la imagen). El title se anima con animate-title-slide
                en cada cambio de previewIndex. */}
            <div className="w-[580px] max-w-full flex items-center justify-between gap-2.5">
              <p
                key={desktopPreviewPreset.id}
                className="text-[19px] font-semibold text-brand-black/90 truncate min-w-0 flex-1 animate-title-slide"
              >
                {desktopPreviewPreset.title}
              </p>
              {/* "Seleccionar varias" pill (ícono Copy) — solo cuando NO
                  estamos en multi mode y el preset actual lo admite. Una
                  vez en multi mode, no hay botón separado para entrar/
                  salir — el botón principal de abajo absorbe la lógica
                  (mismo patrón que en mobile). */}
              {!multiMode &&
                isPresetMultiSelectable(desktopPreviewPreset) && (
                  <button
                    type="button"
                    onClick={() => enterMultiMode(desktopPreviewPreset.id)}
                    aria-label="Seleccionar varias"
                    className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              {/* Botón de acción único — mismo patrón que mobile:
                  • !multiMode → "Generar con mi producto" (utiliza single)
                  • multiMode + currentSelected → "Generar (N)" (confirma)
                  • multiMode + !currentSelected + multi-selectable →
                    "Seleccionar" (añade al set)
                  • multiMode + !multi-selectable → confirma N si hay,
                    sino cae a "Generar con mi producto" single. */}
              <button
                type="button"
                onClick={() => {
                  if (!multiMode) {
                    onUtilizarPreset(desktopPreviewPreset);
                    return;
                  }
                  const canAdd = isPresetMultiSelectable(desktopPreviewPreset);
                  const isSel = isMultiSelected(desktopPreviewPreset.id);
                  if (!canAdd && !isSel) {
                    if (multiSelectedPresets.length > 0) handleConfirmMulti();
                    else onUtilizarPreset(desktopPreviewPreset);
                    return;
                  }
                  if (isSel) handleConfirmMulti();
                  else toggleMultiId(desktopPreviewPreset.id);
                }}
                disabled={multiMode && multiSelectedIds.length === 0 &&
                  !isPresetMultiSelectable(desktopPreviewPreset)}
                className={`shrink-0 inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-md transition-colors ${
                  multiMode &&
                  isMultiSelected(desktopPreviewPreset.id) &&
                  multiSelectedIds.length > 0
                    ? 'pl-5 pr-3'
                    : 'px-6'
                }`}
              >
                {multiMode &&
                !isMultiSelected(desktopPreviewPreset.id) &&
                isPresetMultiSelectable(desktopPreviewPreset) ? (
                  <Plus className="w-5 h-5" strokeWidth={2.5} />
                ) : (
                  <Sparkles className="w-5 h-5" />
                )}
                <span>
                  {!multiMode
                    ? 'Generar con mi producto'
                    : isMultiSelected(desktopPreviewPreset.id)
                      ? 'Generar'
                      : isPresetMultiSelectable(desktopPreviewPreset)
                        ? 'Seleccionar'
                        : multiSelectedPresets.length > 0
                          ? 'Generar'
                          : 'Generar con mi producto'}
                </span>
                {/* Badge circular blanco con el conteo — solo cuando el
                    label efectivo es "Generar" en multi-mode con N items. */}
                {multiMode &&
                  multiSelectedIds.length > 0 &&
                  (isMultiSelected(desktopPreviewPreset.id) ||
                    !isPresetMultiSelectable(desktopPreviewPreset)) && (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-white text-brand-primary text-[13px] font-bold leading-none tabular-nums">
                      {multiSelectedIds.length}
                    </span>
                  )}
              </button>
            </div>
            {multiMode && multiSelectedPresets.length > 0 && (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-4 z-30 w-[640px] max-w-[calc(100%-3rem)] pointer-events-none">
                <div className="pointer-events-auto">
                  <MultiSelectedStrip
                    selected={multiSelectedPresets}
                    onRemove={toggleMultiId}
                    onCancel={exitMultiMode}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          // ── Desktop flujo normal: dos cards (imagen + opciones) ───────
          // En el modo 'design-modelo' a partir del step 'configure'
          // ocultamos la imagen y dejamos solo el aside, centrado y más
          // amplio — son muchas opciones (Modelo + Pose + Fondo + Formato)
          // y la imagen de la izquierda no aporta. La transición es vía
          // `transition-all` sobre width + opacity de la imagen.
          <div
            className={`px-6 pb-6 flex flex-row gap-4 justify-center ${
              isFullScreenConfigure && step === 'configure'
                ? 'items-start'
                : 'flex-1 min-h-0 items-stretch'
            }`}
          >
            {(isDesignFlow || preset.kind === 'catalogo-ia') &&
            !(isFullScreenConfigure && step === 'configure') ? (
              // Layout especial para todos los steps de design-modelo /
              // design-auto / catalogo-ia que muestran la imagen
              // (choose-product, pick-product, upload-product): imagen
              // chica + texto explicativo JUSTO debajo, todo centrado.
              // El card crece a flex-1 para igualar el alto del sidebar.
              // En 'configure' el card se oculta entero (otra rama).
              <div className="flex-1 max-w-[640px] bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] flex flex-col items-center justify-center p-6 gap-2">
                <img
                  src={preset.imageUrlFull || preset.imageUrl}
                  alt={preset.title}
                  // `max-h-[360px]` evita que un asset portrait (como el
                  // hero de catalogo-ia) se vea desproporcionalmente alto
                  // comparado con los landscape de los otros modos. El hero
                  // de anuncio es un collage que se ve mejor un poco más
                  // chico, así que sólo ese baja de tamaño en desktop.
                  className={`${
                    preset.kind === 'anuncio'
                      ? 'max-w-[320px] max-h-[290px]'
                      : 'max-w-[400px] max-h-[360px]'
                  } w-full h-auto object-contain`}
                />
                <p className="text-[12.5px] text-[#3C3C43]/60 leading-relaxed text-center text-balance max-w-[360px]">
                  {preset.kind === 'design-auto'
                    ? 'Atlas elige la escena por vos. Subí tu producto y la IA arma la imagen final en segundos — sin configurar nada.'
                    : preset.kind === 'catalogo-ia'
                      ? 'Generá photoshoots de catálogo para cada producto en un solo proceso. Elegí qué vistas y poses querés generar — Atlas arma una imagen por cada combinación.'
                      : preset.kind === 'anuncio'
                        ? 'Armá un anuncio listo para publicar — texto, colores y formato definidos por vos o por Atlas.'
                        : 'Generá una imagen de tu producto sobre una modelo de IA. Elegí la modelo, la pose y el fondo — Atlas arma la escena en segundos.'}
                </p>
              </div>
            ) : (
              <div
                className={`relative bg-white rounded-2xl overflow-hidden shadow-sm ring-1 ring-black/[0.04] ${
                  isFullScreenConfigure && step === 'configure'
                    ? 'hidden'
                    : 'flex-1 max-w-[640px]'
                }`}
              >
                {isMultiPreset ? (
                  <MultiPresetCarousel presets={multiPresets} fit="cover" />
                ) : preset.outputType === 'video' ? (
                  <video
                    src={preset.imageUrlFull || preset.imageUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster={preset.imageUrl}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={preset.imageUrlFull || preset.imageUrl}
                    alt={preset.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
              </div>
            )}
            <aside
              className={`shrink-0 flex flex-col ${
                isFullScreenConfigure && step === 'configure'
                  ? 'w-full max-w-[1040px] bg-transparent'
                  : 'w-[420px] xl:w-[460px] bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden'
              }`}
            >
              {step === 'pick-product' || step === 'upload-product' ? (
                // Estos paneles manejan su propio layout (header / scroll
                // / footer fijo). NO les metemos padding ni overflow al
                // wrapper — sería doble scroll y el footer no se vería
                // sticky.
                <div key={step} className={`${swapAnimClass} flex-1 min-h-0 flex flex-col`}>
                  {renderStep('desktop')}
                </div>
              ) : (
                // Resto de los steps: wrapper con padding + overflow-y
                // por si el contenido es más alto. `justify-center` solo
                // en choose-product (contenido corto, queda mejor centrado).
                // En design-modelo configure NO scrolleamos internamente
                // — la ventana entera es la scrolleable.
                <div
                  key={step}
                  // pb-6 default — pero en design-flow + configure lo
                  // sacamos para que el Generar sticky del ConfigurePanel
                  // llegue al borde del aside sin gap (mismo fix que en
                  // mobile).
                  className={`${swapAnimClass} px-6 pt-6 flex flex-col ${
                    isDesignFlow && step === 'configure' ? '' : 'pb-6'
                  } ${
                    isFullScreenConfigure && step === 'configure'
                      ? ''
                      : 'flex-1 overflow-y-auto'
                  } ${step === 'choose-product' ? 'justify-center' : ''}`}
                >
                  {renderStep('desktop')}
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </>
  );
};
